import { FC } from "react";
import { StepConversion } from "@models/population.model";

/** What a panel's Total row shows: the group's size, its distinct converters, and the sums
 *  of its rows. Converters are not a sum -- one member converting on three steps is one
 *  member converted and three conversions -- so they come from the server (MPN-1943). */
export interface StepTotals {
  members: number;
  converters: number;
  conversions: number;
  preconversions: number;
  reversions: number;
}

/** The event sums the Total row shows, added here the way the server adds its own. */
export const sumSteps = (steps: StepConversion[]) => ({
  conversions: steps.reduce((acc, s) => acc + s.conversions, 0),
  preconversions: steps.reduce((acc, s) => acc + s.preconversions, 0),
  reversions: steps.reduce((acc, s) => acc + s.reversions, 0),
});

const n = (value: number) => value.toLocaleString();

/** One "Conversions by step" panel. Extracted so Treatment and Held Out are literally
 *  the same component (MPN-1615) -- two hand-written copies drift, and these two exist
 *  to be compared line against line. The tiered panels (MPN-2077) are the same component
 *  again, with the channel as a tag and a Total row closing them. */
const StepPanel: FC<{
  title: string;
  steps: StepConversion[];
  holdout?: boolean;
  tag?: string;
  total?: StepTotals;
}> = ({ title, steps, holdout = false, tag, total }) => (
  <div className={`population-steps${holdout ? " population-steps--holdout" : ""}`}>
    <div className="population-steps__title">
      {title}
      {tag && <span className="population-tag">{tag}</span>}
    </div>
    {steps.map((step, idx) => (
      <div
        key={`${step.journey_step_id}-${step.journey_step_dimension_id}-${idx}`}
        className="population-steps__row"
      >
        <span className="population-steps__name">{step.name}</span>
        {/* All three always render, zeros included: "0 reverted" is a
            finding (nothing regressed), while omitting it reads as "not
            tracked" — and a row showing two counts next to one showing
            three is unreadable. PopulationStats already never hides them. */}
        <span className="population-steps__counts">
          <strong>{n(step.conversions)}</strong>{" "}
          {step.conversions === 1 ? "member" : "members"} converted
          {" · "}{n(step.preconversions)} pre-converted
          {" · "}{n(step.reversions)} reverted
        </span>
      </div>
    ))}
    {total && (
      <div className="population-steps__row population-steps__row--total">
        <span className="population-steps__name">
          Total · {n(total.members)} {total.members === 1 ? "member" : "members"}
        </span>
        <span className="population-steps__counts">
          <strong>{n(total.converters)}</strong>{" "}
          {total.converters === 1 ? "member" : "members"} converted
          {" · "}{n(total.conversions)} total conversions
          {" · "}{n(total.preconversions)} pre-converted
          {" · "}{n(total.reversions)} reverted
        </span>
      </div>
    )}
  </div>
);

export default StepPanel;
